import React from "react";
import { useContext } from "react";

import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Grid,
  Divider,
  Box,
  Chip,
  Paper,
  Avatar,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import Swal from "sweetalert2";
import {
  FaUserGraduate,
  FaHome,
  FaMoneyBillWave,
  FaFileAlt,
  FaUsers,
  FaUserTie,
  FaCheckCircle,
  FaTimesCircle,
  FaInfoCircle,
  FaTrash,
} from "react-icons/fa";
import RejectionFeedbackModal from "./rejection-feedback-modal";
import {
  APPLICATION_STATUS,
  type ScholarshipApplicationDetailsProps,
  type UserRolesEnum,
  type OtherScholarship,
  type StudyingFamilyMember,
  type EarningFamilyMember,
  type PersonalStatement,
  type DocumentAttached,
} from "../../../typings";
import { FlexCol, FlexRow, Spacer } from "../../../components";
import theme from "../../../theme";
import { AuthContext } from "../../../context/AuthContext";
import {
  ADMIN_PERMISSIONS,
  STUDENT_ONLY_PERMISSIONS,
  SUPER_ADMIN_PERMISSIONS,
} from "../../../data/roles";
import api from "../../../api/axiosInstance";
import ApprovalFeedbackModal from "./approval-feeback-modal";
import { mapParentApplicationFields } from "../../../utils";

interface ScholarshipDetailsModalProps {
  open: boolean;
  onClose: () => void;
  application: ScholarshipApplicationDetailsProps;
  onRefresh?: () => void;
}

export default function ScholarshipDetailsModal({
  open,
  onClose,
  application,
  onRefresh,
}: ScholarshipDetailsModalProps) {
  const [loading, setLoading] = useState(false);
  const [rejectionModalOpen, setRejectionModalOpen] = useState(false);
  const [approvalModalOpen, setApprovalModalOpen] = useState(false);
  const [initialRejection, setInitialRejection] = useState(true);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info" | "warning";
  }>({
    open: false,
    message: "",
    severity: "info",
  });
  const auth = useContext(AuthContext);

  const isAdmin = [...ADMIN_PERMISSIONS, ...SUPER_ADMIN_PERMISSIONS].includes(
    auth?.role as UserRolesEnum
  );
  const isStudent = [...STUDENT_ONLY_PERMISSIONS].includes(
    auth?.role as UserRolesEnum
  );

  const interviewFeedbackProvided = () => {
    if (application?.interviews) {
      return application.interviews.some(
        (interview) => interview.feedback.length > 0
      );
    }
    return false;
  };

  const mentorFeedbackProvided = () => {
    if (application?.mentors) {
      return application.mentors.some((mentor) => mentor.feedback.length > 0);
    }
    return false;
  };

  const handleAccept = async (applicationId: number) => {
    try {
      setLoading(true);
      const payload = {
        scholarship_application_id: applicationId,
        is_approved: true,
      };
      const response = await api.post(
        "/scholarship-applications/initial-admin-approval/",
        payload
      );
      
      setSnackbar({
        open: true,
        message: "Application accepted successfully",
        severity: "success",
      });
      
      // Refresh the applications list
      if (onRefresh) onRefresh();
      
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error: any) {
      console.error("Error accepting application:", error);
      
      const errorMessage = error?.response?.data?.message || 
                          error?.response?.data?.detail || 
                          "Failed to accept application. Please try again.";
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReject = (initialRejection: boolean) => {
    setInitialRejection(initialRejection);
    setRejectionModalOpen(true);
  };

  const handleRejectionSubmit = async (
    remarks: string,
    initialRejection: boolean
  ) => {
    // Modal handles the API call, refresh data and close modals
    if (onRefresh) onRefresh();
    setRejectionModalOpen(false);
    onClose();
  };

  const handleApprove = () => {
    setApprovalModalOpen(true);
  };

  const handleApprovalSubmit = async (amount: string) => {
    // Modal handles the API call, refresh data and close modals
    if (onRefresh) onRefresh();
    setApprovalModalOpen(false);
    onClose();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "PKR",
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Apply the mapper to the application object
  const mappedApplication = mapParentApplicationFields(application);

  const SectionTitle = ({
    icon,
    title,
  }: {
    icon: React.ReactNode;
    title: string;
  }) => (
    <Box sx={{ display: "flex", alignItems: "center", mb: 2, mt: 3 }}>
      <Box
        sx={{
          mr: 1.5,
          color: "#3a5a78",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "rgba(58, 90, 120, 0.1)",
          p: 1,
          borderRadius: "50%",
        }}
      >
        {icon}
      </Box>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          color: "#3a5a78",
          position: "relative",
          "&:after": {
            content: '""',
            position: "absolute",
            bottom: -4,
            left: 0,
            width: "40px",
            height: "3px",
            bgcolor: "#3a5a78",
            borderRadius: "2px",
          },
        }}
      >
        {title}
      </Typography>
    </Box>
  );

  const handleDeleteDonor = (donorId: number) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3a5a78",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      customClass: {
        container: "swal-overlay-container",
        popup: "swal-overlay-popup",
      },
      target: document.body,
      backdrop: `rgba(0,0,0,0.4)`,
    }).then((result) => {
      if (result.isConfirmed) {
        setLoading(true);
        api
          .delete("scholarship-applications/donors/", {
            data: {
              scholarship_application_id: application.id,
              donor_id: donorId,
            },
          })
          .then((response) => {
            setSnackbar({
              open: true,
              message: "Donor has been successfully removed",
              severity: "success",
            });
            onClose();
          })
          .catch((error) => {
            console.error("Error deleting donor:", error);
            setSnackbar({
              open: true,
              message: "Failed to delete the donor",
              severity: "error",
            });
          })
          .finally(() => {
            setLoading(false);
          });
      }
    });
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "8px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
          },
        }}
      >
        <DialogTitle
          sx={{
            p: 3,
            bgcolor: "#3a5a78",
            color: "white",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Scholarship Application Details
          </Typography>

          <Chip
            label={application.status}
            icon={
              application.status === "Approved" ? (
                <FaCheckCircle />
              ) : application.status === "Rejected" ? (
                <FaTimesCircle />
              ) : (
                <FaInfoCircle />
              )
            }
            sx={{
              bgcolor:
                application.status === "Approved"
                  ? "#4caf50"
                  : application.status === "Paid"
                  ? "#6938EF"
                  : application.status === "Rejected"
                  ? "#f44336"
                  : "#ff9800",
              color: "white",
              fontWeight: 600,
              padding: "4px",
              "& .MuiChip-icon": { color: "white" },
            }}
          />
        </DialogTitle>
        <Spacer vertical={0.5} />
        <DialogContent sx={{ p: 3 }}>
          {application.status === "Rejected" &&
            application.rejection_reason && (
              <Box
                sx={{
                  mb: 3,
                  p: 3,
                  bgcolor: "#ffebee",
                  borderRadius: "8px",
                  border: "1px solid #ffcdd2",
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: "#d32f2f",
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <FaTimesCircle style={{ marginRight: "8px" }} /> Rejection
                  Reason
                </Typography>
                <Typography variant="body2">
                  {application.rejection_reason}
                </Typography>
              </Box>
            )}
          {/* Personal Information */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "center", sm: "flex-start" },
              mb: 3,
              p: 3,
              borderRadius: "12px",
            }}
          >
            <Avatar
              src={mappedApplication.photo}
              sx={{
                width: 120,
                height: 120,
                mr: { xs: 0, sm: 3 },
                mb: { xs: 2, sm: 0 },
                border: "4px solid white",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
              }}
              alt={application.name}
            />
            <Box sx={{ textAlign: { xs: "center", sm: "left" } }}>
              <FlexRow align="center">
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 700, color: "#3a5a78", mb: 0.5 }}
                >
                  {application.name}
                </Typography>
                {application.is_special_case && (
                  <>
                    <Spacer horizontal={1} />
                    <Chip
                      size="small"
                      label={`Special Case`}
                      sx={{ bgcolor: "#D0F0C0", color: "#3a5a78" }}
                    />
                  </>
                )}
                {application.parent_application?.id && (
                  <>
                    <Spacer horizontal={1} />
                    <Chip
                      size="small"
                      label={`Parent Application ID: ${application.parent_application.id}`}
                      sx={{
                        bgcolor: "rgba(58, 90, 120, 0.1)",
                        color: "#3a5a78",
                      }}
                    />
                  </>
                )}
              </FlexRow>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: { xs: "center", sm: "flex-start" },
                  gap: 1,
                  mb: 1,
                }}
              >
                <Chip
                  size="small"
                  label={`Age: ${application.age}`}
                  sx={{ bgcolor: "rgba(58, 90, 120, 0.1)", color: "#3a5a78" }}
                />
                <Chip
                  size="small"
                  label={`Gender: ${application.gender}`}
                  sx={{ bgcolor: "rgba(58, 90, 120, 0.1)", color: "#3a5a78" }}
                />
                <Chip
                  size="small"
                  label={`CNIC: ${application.cnic_number}`}
                  sx={{ bgcolor: "rgba(58, 90, 120, 0.1)", color: "#3a5a78" }}
                />
                {application.auto_evaluation_score && (
                  <Chip
                    size="small"
                    label={`Auto Evaluation Score: ${application.auto_evaluation_score}`}
                    sx={{
                      bgcolor: "rgba(58, 90, 120, 0.1)",
                      color: "#3a5a78",
                    }}
                  />
                )}
                {application.social_participation_score > 0 && (
                  <Chip
                    size="small"
                    label={`Social Participation Score: ${application.social_participation_score}`}
                    sx={{
                      bgcolor: "rgba(58, 90, 120, 0.1)",
                      color: "#3a5a78",
                    }}
                  />
                )}
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: { xs: "center", sm: "flex-start" },
                  mb: 0.5,
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <Box
                    component="span"
                    sx={{ mr: 0.5, display: "inline-flex", color: "#3a5a78" }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                    </svg>
                  </Box>
                  {application.cell_phone}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: { xs: "center", sm: "flex-start" },
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <Box
                    component="span"
                    sx={{ mr: 0.5, display: "inline-flex", color: "#3a5a78" }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                      <polyline points="22,6 12,13 2,6"></polyline>
                    </svg>
                  </Box>
                  {application.email}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: { xs: "center", sm: "flex-start" },
                  mt: 0.5,
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <Box
                    component="span"
                    sx={{ mr: 0.5, display: "inline-flex", color: "#3a5a78" }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                      <circle cx="12" cy="10" r="3"></circle>
                    </svg>
                  </Box>
                  {application.residential_address}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Divider />

          {/* Basic Details */}

          {/* Educational History */}
          {mappedApplication.educational_history.length > 0 && (
            <>
              <SectionTitle
                icon={<FaUserGraduate size={20} />}
                title="Educational History"
              />
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: "8px",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                  transition: "all 0.3s ease",
                  "&:hover": { boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)" },
                }}
              >
                {mappedApplication.educational_history.map((edu, index) => (
                  <Box
                    key={index}
                    sx={{
                      mb:
                        index < mappedApplication.educational_history.length - 1
                          ? 2
                          : 0,
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {edu.institute}
                    </Typography>
                    <Typography variant="body2">
                      {edu.score_is_percentage
                        ? `Percentage: ${edu.score}%`
                        : `CGPA: ${edu.score}`}
                    </Typography>
                    <Typography variant="body2">
                      Level:{" "}
                      {edu.is_university
                        ? "University"
                        : edu.is_higher_school
                        ? "Higher School"
                        : "Secondary School"}
                    </Typography>
                    {index <
                      mappedApplication.educational_history.length - 1 && (
                      <Divider sx={{ my: 1 }} />
                    )}
                  </Box>
                ))}
              </Paper>
            </>
          )}

          {/* Scholarship Request Details */}
          <SectionTitle
            icon={<FaFileAlt size={20} />}
            title="Scholarship Request Details"
          />
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
              transition: "all 0.3s ease",
              "&:hover": { boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)" },
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>Degree:</strong>{" "}
                  {application.scholarship_request_details.degree_name}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>Discipline:</strong>{" "}
                  {application.scholarship_request_details.degree_discipline}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>Institute:</strong>{" "}
                  {application.scholarship_request_details.degree_institute}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>Recognized By:</strong>{" "}
                  {application.scholarship_request_details.degree_recognized_by}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>Tuition Fee:</strong>{" "}
                  {formatCurrency(
                    application.scholarship_request_details
                      .tuition_fee_per_semester
                  )}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>Admission Fee:</strong>{" "}
                  {formatCurrency(
                    application.scholarship_request_details.admission_fee
                  )}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>Self Contribution:</strong>{" "}
                  {
                    application.scholarship_request_details
                      .self_contribution_percentage
                  }
                  %
                </Typography>
              </Grid>
              {application.scholarship_request_details?.degree_semester && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2">
                    <strong>Semester:</strong>{" "}
                    {application.scholarship_request_details.degree_semester}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Paper>

          {/* Other Scholarships */}
          {mappedApplication.other_scholarships.length > 0 && (
            <>
              <SectionTitle
                icon={<FaMoneyBillWave size={20} />}
                title="Other Scholarships"
              />
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: "8px",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                  transition: "all 0.3s ease",
                  "&:hover": { boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)" },
                }}
              >
                {mappedApplication.other_scholarships.map(
                  (scholarship: OtherScholarship, index: number) => (
                    <Box
                      key={index}
                      sx={{
                        mb:
                          index <
                          mappedApplication.other_scholarships.length - 1
                            ? 2
                            : 0,
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {scholarship.institute_name}
                      </Typography>
                      <Typography variant="body2">
                        Amount: {formatCurrency(scholarship.total_amount)}
                      </Typography>
                      <Typography variant="body2">
                        Duration: {scholarship.duration_in_years} years
                      </Typography>
                      <Typography variant="body2">
                        Class/Degree: {scholarship.class_or_degree}
                      </Typography>
                      {index <
                        mappedApplication.other_scholarships.length - 1 && (
                        <Divider sx={{ my: 1 }} />
                      )}
                    </Box>
                  )
                )}
              </Paper>
            </>
          )}

          {/* Guardian Details */}
          <SectionTitle
            icon={<FaUserTie size={20} />}
            title="Guardian Details"
          />
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
              transition: "all 0.3s ease",
              "&:hover": { boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)" },
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>Name:</strong> {application.guardian_details.name}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>CNIC:</strong>{" "}
                  {application.guardian_details.cnic_number}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>Cell Phone:</strong>{" "}
                  {application.guardian_details.cell_phone}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>Occupation:</strong>{" "}
                  {application.guardian_details.occupation}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>Designation:</strong>{" "}
                  {application.guardian_details.designation}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>Employer:</strong>{" "}
                  {application.guardian_details.employer}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>Monthly Income:</strong>{" "}
                  {formatCurrency(application.guardian_details.monthly_income)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>Pension:</strong>{" "}
                  {formatCurrency(application.guardian_details.pension)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2">
                  <strong>Family Members:</strong>{" "}
                  {application.guardian_details.family_members_count}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Family Members */}
          {((mappedApplication.studying_family_members &&
            mappedApplication.studying_family_members.length > 0) ||
            (mappedApplication.earning_family_members &&
              mappedApplication.earning_family_members.length > 0)) && (
            <SectionTitle icon={<FaUsers size={20} />} title="Family Members" />
          )}

          {/* Studying Family Members */}
          {mappedApplication.studying_family_members &&
            mappedApplication.studying_family_members.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Studying Family Members
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: "8px",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                    transition: "all 0.3s ease",
                    "&:hover": { boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)" },
                  }}
                >
                  {mappedApplication.studying_family_members.map(
                    (member: StudyingFamilyMember, index: number) => (
                      <Box
                        key={index}
                        sx={{
                          mb:
                            index <
                            mappedApplication.studying_family_members.length - 1
                              ? 2
                              : 0,
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, color: "#3a5a78" }}
                        >
                          {member.name}
                        </Typography>
                        <Typography variant="body2">
                          <strong>{member.relation}:</strong> {member.degree} at{" "}
                          {member.institute}
                        </Typography>
                        <Typography variant="body2">
                          Fee: {formatCurrency(member.fee)}
                        </Typography>
                        {index <
                          mappedApplication.studying_family_members.length -
                            1 && <Divider sx={{ my: 1 }} />}
                      </Box>
                    )
                  )}
                </Paper>
              </Box>
            )}

          {/* Earning Family Members */}
          {mappedApplication.earning_family_members &&
            mappedApplication.earning_family_members.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Earning Family Members
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: "8px",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                    transition: "all 0.3s ease",
                    "&:hover": { boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)" },
                  }}
                >
                  {mappedApplication.earning_family_members.map(
                    (member: EarningFamilyMember, index: number) => (
                      <Box
                        key={index}
                        sx={{
                          mb:
                            index <
                            mappedApplication.earning_family_members.length - 1
                              ? 2
                              : 0,
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, color: "#3a5a78" }}
                        >
                          {member.name}
                        </Typography>
                        <Typography variant="body2">
                          <strong>{member.relation}:</strong>{" "}
                          {member.occupation} at {member.organization}
                        </Typography>
                        <Typography variant="body2">
                          Income: {formatCurrency(member.income)}
                        </Typography>
                        {index <
                          mappedApplication.earning_family_members.length -
                            1 && <Divider sx={{ my: 1 }} />}
                      </Box>
                    )
                  )}
                </Paper>
              </Box>
            )}

          {/* Accommodation Details */}
          <SectionTitle
            icon={<FaHome size={20} />}
            title="Accommodation Details"
          />
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
              transition: "all 0.3s ease",
              "&:hover": { boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)" },
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>Type:</strong>{" "}
                  {mappedApplication.accommodation_details.accommodation_type}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>Ownership:</strong>{" "}
                  {
                    mappedApplication.accommodation_details
                      .accommodation_ownership
                  }
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>Joint/Independent:</strong>{" "}
                  {
                    mappedApplication.accommodation_details
                      .accommodation_ownership_joint_or_independent
                  }
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>Monthly Rent:</strong>{" "}
                  {formatCurrency(
                    mappedApplication.accommodation_details.monthly_rent
                  )}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Financial Information */}
          {(mappedApplication.family_liabilities.length > 0 ||
            mappedApplication.family_expenses.length > 0) && (
            <SectionTitle
              icon={<FaMoneyBillWave size={20} />}
              title="Financial Information"
            />
          )}

          {/* Family Liabilities */}
          {mappedApplication.family_liabilities.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Family Liabilities
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: "8px",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                  transition: "all 0.3s ease",
                  "&:hover": { boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)" },
                }}
              >
                {mappedApplication.family_liabilities.map(
                  (liability, index) => (
                    <Box
                      key={index}
                      sx={{
                        mb:
                          index <
                          mappedApplication.family_liabilities.length - 1
                            ? 2
                            : 0,
                      }}
                    >
                      <Typography variant="body2">
                        <strong>{liability.nature}:</strong>{" "}
                        {formatCurrency(liability.amount)}
                      </Typography>
                      <Typography variant="body2">
                        {liability.description}
                      </Typography>
                      {index <
                        mappedApplication.family_liabilities.length - 1 && (
                        <Divider sx={{ my: 1 }} />
                      )}
                    </Box>
                  )
                )}
              </Paper>
            </Box>
          )}

          {/* Family Expenses */}
          {mappedApplication.family_expenses.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Family Expenses
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: "8px",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                  transition: "all 0.3s ease",
                  "&:hover": { boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)" },
                }}
              >
                {mappedApplication.family_expenses.map((expense, index) => (
                  <Box
                    key={index}
                    sx={{
                      mb:
                        index < mappedApplication.family_expenses.length - 1
                          ? 2
                          : 0,
                    }}
                  >
                    <Typography variant="body2">
                      <strong>{expense.nature}:</strong>{" "}
                      {formatCurrency(expense.amount)}
                    </Typography>
                    <Typography variant="body2">
                      {expense.description}
                    </Typography>
                    {index < mappedApplication.family_expenses.length - 1 && (
                      <Divider sx={{ my: 1 }} />
                    )}
                  </Box>
                ))}
              </Paper>
            </Box>
          )}

          {/* Personal Statements */}

          <SectionTitle
            icon={<FaInfoCircle size={20} />}
            title="Personal Statements"
          />
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
              transition: "all 0.3s ease",
              "&:hover": { boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)" },
            }}
          >
            {mappedApplication.personal_statements.map(
              (statement: PersonalStatement, index: number) => (
                <Box
                  key={index}
                  sx={{
                    mb:
                      index < mappedApplication.personal_statements.length - 1
                        ? 2
                        : 0,
                    p: 2,
                    borderRadius: "8px",
                    bgcolor: "rgba(58, 90, 120, 0.05)",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: "#3a5a78",
                      pb: 1,
                      borderBottom: "1px dashed rgba(58, 90, 120, 0.3)",
                      mb: 1,
                    }}
                  >
                    {statement.question}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 1,
                      fontStyle: "italic",
                      color: "#555",
                      pl: 1,
                      borderLeft: "3px solid rgba(58, 90, 120, 0.3)",
                    }}
                  >
                    {statement.response}
                  </Typography>
                  {index < mappedApplication.personal_statements.length - 1 && (
                    <Box sx={{ my: 2 }} />
                  )}
                </Box>
              )
            )}
          </Paper>

          {/* Interviews */}
          {application.interviews && application.interviews.length > 0 && (
            <>
              <SectionTitle icon={<FaUserTie size={20} />} title="Interviews" />
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: "8px",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                  transition: "all 0.3s ease",
                  "&:hover": { boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)" },
                }}
              >
                {application.interviews.map((interview, index) => (
                  <Box
                    key={index}
                    sx={{
                      mb: index < application.interviews.length - 1 ? 2 : 0,
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Interview with {interview.interviewer.name} on{" "}
                      {formatDate(interview.interview_date)}
                    </Typography>
                    <Typography variant="body2">
                      Scheduled by: {interview.scheduled_by.name}
                    </Typography>

                    {interview.feedback.length > 0 ? (
                      <Box sx={{ mt: 1 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600 }}
                        >
                          Feedback:
                        </Typography>
                        {interview.feedback.map((feedback, fidx) => (
                          <Box
                            key={fidx}
                            sx={{
                              mt: 2,
                              p: 2,
                              bgcolor: "rgba(58, 90, 120, 0.05)",
                              borderRadius: "8px",
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 600,
                                mb: 2,
                                color: "#3a5a78",
                              }}
                            >
                              Academic Performance
                            </Typography>
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                  <strong>Commitment to Studies:</strong>
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: "100%",
                                      height: "8px",
                                      bgcolor: "rgba(0,0,0,0.1)",
                                      borderRadius: "4px",
                                      mr: 1,
                                      position: "relative",
                                      overflow: "hidden",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        height: "100%",
                                        width: `${
                                          (feedback.commitment_to_studies_rating /
                                            5) *
                                          100
                                        }%`,
                                        bgcolor: "#3a5a78",
                                        borderRadius: "4px",
                                      }}
                                    />
                                  </Box>
                                  <Typography
                                    variant="body2"
                                    sx={{ minWidth: "24px" }}
                                  >
                                    {feedback.commitment_to_studies_rating}/5
                                  </Typography>
                                </Box>
                              </Grid>
                            </Grid>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                              {
                                feedback.comments_related_to_academic_performance
                              }
                            </Typography>

                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 600,
                                mb: 2,
                                color: "#3a5a78",
                                mt: 3,
                              }}
                            >
                              Leadership & Community
                            </Typography>
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                              <Grid item xs={12} sm={4}>
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                  <strong>Leadership Potential:</strong>
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: "100%",
                                      height: "8px",
                                      bgcolor: "rgba(0,0,0,0.1)",
                                      borderRadius: "4px",
                                      mr: 1,
                                      position: "relative",
                                      overflow: "hidden",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        height: "100%",
                                        width: `${
                                          (feedback.leadership_potential_rating /
                                            5) *
                                          100
                                        }%`,
                                        bgcolor: "#3a5a78",
                                        borderRadius: "4px",
                                      }}
                                    />
                                  </Box>
                                  <Typography
                                    variant="body2"
                                    sx={{ minWidth: "24px" }}
                                  >
                                    {feedback.leadership_potential_rating}/5
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={12} sm={4}>
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                  <strong>Community Involvement:</strong>
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: "100%",
                                      height: "8px",
                                      bgcolor: "rgba(0,0,0,0.1)",
                                      borderRadius: "4px",
                                      mr: 1,
                                      position: "relative",
                                      overflow: "hidden",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        height: "100%",
                                        width: `${
                                          (feedback.community_involvement_rating /
                                            5) *
                                          100
                                        }%`,
                                        bgcolor: "#3a5a78",
                                        borderRadius: "4px",
                                      }}
                                    />
                                  </Box>
                                  <Typography
                                    variant="body2"
                                    sx={{ minWidth: "24px" }}
                                  >
                                    {feedback.community_involvement_rating}/5
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={12} sm={4}>
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                  <strong>Initiative & Creativity:</strong>
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: "100%",
                                      height: "8px",
                                      bgcolor: "rgba(0,0,0,0.1)",
                                      borderRadius: "4px",
                                      mr: 1,
                                      position: "relative",
                                      overflow: "hidden",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        height: "100%",
                                        width: `${
                                          (feedback.initiative_and_creativity_rating /
                                            5) *
                                          100
                                        }%`,
                                        bgcolor: "#3a5a78",
                                        borderRadius: "4px",
                                      }}
                                    />
                                  </Box>
                                  <Typography
                                    variant="body2"
                                    sx={{ minWidth: "24px" }}
                                  >
                                    {feedback.initiative_and_creativity_rating}
                                    /5
                                  </Typography>
                                </Box>
                              </Grid>
                            </Grid>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                              {feedback.comments_related_to_leadership}
                            </Typography>

                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 600,
                                mb: 2,
                                color: "#3a5a78",
                                mt: 3,
                              }}
                            >
                              Values & Vision
                            </Typography>
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                  <strong>
                                    Alignment with Scholarship Objectives:
                                  </strong>
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: "100%",
                                      height: "8px",
                                      bgcolor: "rgba(0,0,0,0.1)",
                                      borderRadius: "4px",
                                      mr: 1,
                                      position: "relative",
                                      overflow: "hidden",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        height: "100%",
                                        width: `${
                                          (feedback.alignment_with_scholarship_objectives_rating /
                                            5) *
                                          100
                                        }%`,
                                        bgcolor: "#3a5a78",
                                        borderRadius: "4px",
                                      }}
                                    />
                                  </Box>
                                  <Typography
                                    variant="body2"
                                    sx={{ minWidth: "24px" }}
                                  >
                                    {
                                      feedback.alignment_with_scholarship_objectives_rating
                                    }
                                    /5
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                  <strong>
                                    Vision for Future Contribution:
                                  </strong>
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: "100%",
                                      height: "8px",
                                      bgcolor: "rgba(0,0,0,0.1)",
                                      borderRadius: "4px",
                                      mr: 1,
                                      position: "relative",
                                      overflow: "hidden",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        height: "100%",
                                        width: `${
                                          (feedback.vision_for_future_contribution_to_society_rating /
                                            5) *
                                          100
                                        }%`,
                                        bgcolor: "#3a5a78",
                                        borderRadius: "4px",
                                      }}
                                    />
                                  </Box>
                                  <Typography
                                    variant="body2"
                                    sx={{ minWidth: "24px" }}
                                  >
                                    {
                                      feedback.vision_for_future_contribution_to_society_rating
                                    }
                                    /5
                                  </Typography>
                                </Box>
                              </Grid>
                            </Grid>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                              {feedback.comments_related_to_values}
                            </Typography>

                            <Box
                              sx={{
                                mt: 3,
                                p: 2,
                                bgcolor: "rgba(58, 90, 120, 0.1)",
                                borderRadius: "8px",
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600, color: "#3a5a78" }}
                              >
                                Final Remarks
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ mt: 1, fontStyle: "italic" }}
                              >
                                {feedback.final_remarks}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{ fontStyle: "italic", mt: 1 }}
                      >
                        No feedback provided yet
                      </Typography>
                    )}

                    {index < application.interviews.length - 1 && (
                      <Divider sx={{ my: 1 }} />
                    )}
                  </Box>
                ))}
              </Paper>
            </>
          )}

          {/* Mentors Assigned */}
          {application.mentors && application.mentors.length > 0 && (
            <>
              <SectionTitle
                icon={<FaUserGraduate size={20} />}
                title="Mentors Assigned"
              />
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: "8px",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                  transition: "all 0.3s ease",
                  "&:hover": { boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)" },
                }}
              >
                {application.mentors.map((mentorAssignment, index) => (
                  <Box
                    key={index}
                    sx={{
                      mb: index < application.mentors.length - 1 ? 2 : 0,
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Mentor: {mentorAssignment.mentor.name}
                    </Typography>
                    <Typography variant="body2">
                      Assigned by: {mentorAssignment.assigned_by.name}
                    </Typography>

                    {mentorAssignment.feedback.length > 0 ? (
                      <Box sx={{ mt: 1 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600 }}
                        >
                          Feedback:
                        </Typography>
                        {mentorAssignment.feedback.map((feedback, fidx) => (
                          <Box
                            key={fidx}
                            sx={{
                              mt: 2,
                              p: 2,
                              bgcolor: "rgba(58, 90, 120, 0.05)",
                              borderRadius: "8px",
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 600,
                                mb: 2,
                                color: "#3a5a78",
                              }}
                            >
                              Personal Development
                            </Typography>
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                  <strong>Future Goals:</strong>
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: "100%",
                                      height: "8px",
                                      bgcolor: "rgba(0,0,0,0.1)",
                                      borderRadius: "4px",
                                      mr: 1,
                                      position: "relative",
                                      overflow: "hidden",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        height: "100%",
                                        width: `${
                                          (feedback.future_goals_rating / 5) *
                                          100
                                        }%`,
                                        bgcolor: "#3a5a78",
                                        borderRadius: "4px",
                                      }}
                                    />
                                  </Box>
                                  <Typography
                                    variant="body2"
                                    sx={{ minWidth: "24px" }}
                                  >
                                    {feedback.future_goals_rating}/5
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                  <strong>Career Awareness:</strong>
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: "100%",
                                      height: "8px",
                                      bgcolor: "rgba(0,0,0,0.1)",
                                      borderRadius: "4px",
                                      mr: 1,
                                      position: "relative",
                                      overflow: "hidden",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        height: "100%",
                                        width: `${
                                          (feedback.career_awareness_rating /
                                            5) *
                                          100
                                        }%`,
                                        bgcolor: "#3a5a78",
                                        borderRadius: "4px",
                                      }}
                                    />
                                  </Box>
                                  <Typography
                                    variant="body2"
                                    sx={{ minWidth: "24px" }}
                                  >
                                    {feedback.career_awareness_rating}/5
                                  </Typography>
                                </Box>
                              </Grid>
                            </Grid>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                              {feedback.comments_related_to_future_goals}
                            </Typography>

                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 600,
                                mb: 2,
                                color: "#3a5a78",
                                mt: 3,
                              }}
                            >
                              Social Impact & Skills
                            </Typography>
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                              <Grid item xs={12} sm={4}>
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                  <strong>Social Contribution:</strong>
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: "100%",
                                      height: "8px",
                                      bgcolor: "rgba(0,0,0,0.1)",
                                      borderRadius: "4px",
                                      mr: 1,
                                      position: "relative",
                                      overflow: "hidden",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        height: "100%",
                                        width: `${
                                          (feedback.social_contribution_rating /
                                            5) *
                                          100
                                        }%`,
                                        bgcolor: "#3a5a78",
                                        borderRadius: "4px",
                                      }}
                                    />
                                  </Box>
                                  <Typography
                                    variant="body2"
                                    sx={{ minWidth: "24px" }}
                                  >
                                    {feedback.social_contribution_rating}/5
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={12} sm={4}>
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                  <strong>Resilience:</strong>
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: "100%",
                                      height: "8px",
                                      bgcolor: "rgba(0,0,0,0.1)",
                                      borderRadius: "4px",
                                      mr: 1,
                                      position: "relative",
                                      overflow: "hidden",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        height: "100%",
                                        width: `${
                                          (feedback.resilience_rating / 5) * 100
                                        }%`,
                                        bgcolor: "#3a5a78",
                                        borderRadius: "4px",
                                      }}
                                    />
                                  </Box>
                                  <Typography
                                    variant="body2"
                                    sx={{ minWidth: "24px" }}
                                  >
                                    {feedback.resilience_rating}/5
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={12} sm={4}>
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                  <strong>Skills Development:</strong>
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: "100%",
                                      height: "8px",
                                      bgcolor: "rgba(0,0,0,0.1)",
                                      borderRadius: "4px",
                                      mr: 1,
                                      position: "relative",
                                      overflow: "hidden",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        height: "100%",
                                        width: `${
                                          (feedback.skills_development_rating /
                                            5) *
                                          100
                                        }%`,
                                        bgcolor: "#3a5a78",
                                        borderRadius: "4px",
                                      }}
                                    />
                                  </Box>
                                  <Typography
                                    variant="body2"
                                    sx={{ minWidth: "24px" }}
                                  >
                                    {feedback.skills_development_rating}/5
                                  </Typography>
                                </Box>
                              </Grid>
                            </Grid>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                              {feedback.comments_related_to_social_contribution}
                            </Typography>

                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 600,
                                mb: 2,
                                color: "#3a5a78",
                                mt: 3,
                              }}
                            >
                              Future Readiness
                            </Typography>
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                  <strong>Future Education Readiness:</strong>
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: "100%",
                                      height: "8px",
                                      bgcolor: "rgba(0,0,0,0.1)",
                                      borderRadius: "4px",
                                      mr: 1,
                                      position: "relative",
                                      overflow: "hidden",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        height: "100%",
                                        width: `${
                                          (feedback.future_education_readiness_rating /
                                            5) *
                                          100
                                        }%`,
                                        bgcolor: "#3a5a78",
                                        borderRadius: "4px",
                                      }}
                                    />
                                  </Box>
                                  <Typography
                                    variant="body2"
                                    sx={{ minWidth: "24px" }}
                                  >
                                    {feedback.future_education_readiness_rating}
                                    /5
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                  <strong>Overall Rating:</strong>
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: "100%",
                                      height: "8px",
                                      bgcolor: "rgba(0,0,0,0.1)",
                                      borderRadius: "4px",
                                      mr: 1,
                                      position: "relative",
                                      overflow: "hidden",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        height: "100%",
                                        width: `${
                                          (feedback.overall_rating / 30) * 100
                                        }%`,
                                        bgcolor: "#3a5a78",
                                        borderRadius: "4px",
                                      }}
                                    />
                                  </Box>
                                  <Typography
                                    variant="body2"
                                    sx={{ minWidth: "24px" }}
                                  >
                                    {feedback.overall_rating}/30
                                  </Typography>
                                </Box>
                              </Grid>
                            </Grid>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                              {
                                feedback.comments_related_to_future_education_readiness
                              }
                            </Typography>

                            <Box
                              sx={{
                                mt: 3,
                                p: 2,
                                bgcolor: "rgba(58, 90, 120, 0.1)",
                                borderRadius: "8px",
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600, color: "#3a5a78" }}
                              >
                                Final Remarks
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ mt: 1, fontStyle: "italic" }}
                              >
                                {feedback.final_remarks}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{ fontStyle: "italic", mt: 1 }}
                      >
                        No feedback provided yet
                      </Typography>
                    )}

                    {index < application.mentors.length - 1 && (
                      <Divider sx={{ my: 1 }} />
                    )}
                  </Box>
                ))}
              </Paper>
            </>
          )}

          {/* Donors Tagged */}
          {application.donors_tagged &&
            application.donors_tagged.length > 0 && (
              <>
                <SectionTitle
                  icon={<FaUsers size={20} />}
                  title="Donors Tagged"
                />
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: "8px",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                    transition: "all 0.3s ease",
                    "&:hover": { boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)" },
                  }}
                >
                  <Grid container spacing={2}>
                    {application.donors_tagged.map((donor, index) => (
                      <Grid item xs={12} sm={6} md={4} key={index}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            borderRadius: "8px",
                            border: "1px solid #e0e0e0",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            position: "relative",
                            transition: "all 0.2s ease",
                            background:
                              "linear-gradient(145deg, rgba(58, 90, 120, 0.03) 0%, rgba(58, 90, 120, 0.08) 100%)",
                            "&:hover": {
                              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                              borderColor: "#3a5a78",
                            },
                          }}
                        >
                          <Button
                            size="small"
                            onClick={() =>
                              handleDeleteDonor(donor.donor_tagged.id)
                            }
                            sx={{
                              minWidth: "auto",
                              p: 0.5,
                              position: "absolute",
                              top: 8,
                              right: 8,
                              color: "rgba(0, 0, 0, 0.54)",
                              "&:hover": {
                                backgroundColor: "rgba(0, 0, 0, 0.04)",
                                color: "#f44336",
                              },
                            }}
                          >
                            <FaTrash color="#f44336" size={14} />
                          </Button>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 1,
                            }}
                          >
                            <Avatar
                              sx={{
                                width: 40,
                                height: 40,
                                bgcolor: "rgba(58, 90, 120, 0.2)",
                                color: "#3a5a78",
                                mr: 1.5,
                                fontWeight: "bold",
                              }}
                            >
                              {donor.donor_tagged.name.charAt(0)}
                            </Avatar>
                            <Box>
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600, color: "#3a5a78" }}
                              >
                                {donor.donor_tagged.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                Tagged by: {donor.tagged_by.name}
                              </Typography>
                            </Box>
                          </Box>
                          <Chip
                            label={
                              donor.tagged_amount
                                ? formatCurrency(donor.tagged_amount)
                                : "No Amount specified"
                            }
                            sx={{
                              bgcolor: donor.tagged_amount
                                ? "rgba(76, 175, 80, 0.1)"
                                : "rgba(158, 158, 158, 0.1)",
                              color: donor.tagged_amount
                                ? "#2e7d32"
                                : "#616161",
                              fontWeight: 600,
                              mt: 1,
                              alignSelf: "flex-start",
                            }}
                          />
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </>
            )}

          {/* Documents */}
          <SectionTitle icon={<FaFileAlt size={20} />} title="Documents" />
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
              transition: "all 0.3s ease",
              "&:hover": {
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
              },
            }}
          >
            <Grid container spacing={2}>
              {mappedApplication.documents_attached.map(
                (doc: DocumentAttached, index: number) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: "8px",
                        border: "1px solid #e0e0e0",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                          borderColor: "#3a5a78",
                        },
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                      >
                        <FaFileAlt
                          size={16}
                          style={{ color: "#3a5a78", marginRight: "8px" }}
                        />
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, color: "#3a5a78" }}
                        >
                          {doc.title}
                        </Typography>
                      </Box>
                      <Box sx={{ mt: "auto", pt: 1 }}>
                        {doc.attachment_url.map((url: string, urlIndex: number) => (
                          <Button
                            key={urlIndex}
                            variant="outlined"
                            size="small"
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              borderColor: "#3a5a78",
                              color: "#3a5a78",
                              "&:hover": {
                                borderColor: "#3a5a78",
                                backgroundColor: "rgba(58, 90, 120, 0.1)",
                              },
                              width: "100%",
                              mb: urlIndex < doc.attachment_url.length - 1 ? 1 : 0,
                            }}
                          >
                            {doc.attachment_url.length > 1 
                              ? `View Document ${urlIndex + 1}` 
                              : "View Document"}
                          </Button>
                        ))}
                      </Box>
                    </Paper>
                  </Grid>
                )
              )}
            </Grid>
          </Paper>

          {/* Extra Documents Section - Admin Only */}
          {isAdmin &&
            application.extra_documents_attached &&
            application.extra_documents_attached.length > 0 && (
              <>
                <SectionTitle
                  icon={<FaFileAlt size={20} />}
                  title="Extra Documents"
                />
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: "8px",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                    },
                  }}
                >
                  <Grid container spacing={2}>
                    {application.extra_documents_attached.map(
                      (doc: DocumentAttached, index: number) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 2,
                              borderRadius: "8px",
                              border: "1px solid #e0e0e0",
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                              transition: "all 0.2s ease",
                              "&:hover": {
                                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                                borderColor: "#3a5a78",
                              },
                            }}
                          >
                            <Box
                              sx={{ display: "flex", alignItems: "center", mb: 1 }}
                            >
                              <FaFileAlt
                                size={16}
                                style={{ color: "#3a5a78", marginRight: "8px" }}
                              />
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600, color: "#3a5a78" }}
                              >
                                {doc.title || `Extra Document ${index + 1}`}
                              </Typography>
                            </Box>
                            <Box sx={{ mt: "auto", pt: 1 }}>
                              {doc.attachment_url.map(
                                (url: string, urlIndex: number) => (
                                  <Button
                                    key={urlIndex}
                                    variant="outlined"
                                    size="small"
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                      borderColor: "#3a5a78",
                                      color: "#3a5a78",
                                      "&:hover": {
                                        borderColor: "#3a5a78",
                                        backgroundColor: "rgba(58, 90, 120, 0.1)",
                                      },
                                      width: "100%",
                                      mb:
                                        urlIndex < doc.attachment_url.length - 1
                                          ? 1
                                          : 0,
                                    }}
                                  >
                                    {doc.attachment_url.length > 1
                                      ? `View Document ${urlIndex + 1}`
                                      : "View Document"}
                                  </Button>
                                )
                              )}
                            </Box>
                          </Paper>
                        </Grid>
                      )
                    )}
                  </Grid>
                </Paper>
              </>
            )}

          {/* Comments Section */}
          {application.comments && application.comments.length > 0 && (
            <>
              <SectionTitle
                icon={<FaInfoCircle size={20} />}
                title="Comments"
              />
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: "8px",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                  transition: "all 0.3s ease",
                  "&:hover": { boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)" },
                }}
              >
                {application.comments.map((comment, index) => (
                  <Box
                    key={index}
                    sx={{
                      mb: index < application.comments.length - 1 ? 2 : 0,
                      p: 2,
                      borderRadius: "8px",
                      bgcolor: "rgba(58, 90, 120, 0.05)",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <Avatar
                        sx={{
                          width: 36,
                          height: 36,
                          bgcolor: "rgba(58, 90, 120, 0.2)",
                          color: "#3a5a78",
                          mr: 1.5,
                          fontWeight: "bold",
                        }}
                        // src={comment.comment_by.profile_picture_url}
                      >
                        {comment.comment_by.name.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, color: "#3a5a78" }}
                        >
                          {comment.comment_by.name}
                        </Typography>
                        {!isStudent && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            {comment.comment_by.role}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 1,
                        pl: 1,
                        borderLeft: "3px solid rgba(58, 90, 120, 0.3)",
                      }}
                    >
                      {comment.comment}
                    </Typography>
                    {index < application.comments.length - 1 && (
                      <Divider sx={{ mt: 2 }} />
                    )}
                  </Box>
                ))}
              </Paper>
            </>
          )}
        </DialogContent>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            p: 3,
            bgcolor: "#f5f5f5",
            borderTop: "1px solid #e0e0e0",
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              mr: 2,
              color: theme.palette.colors["white"]["400"],
              backgroundColor: theme.palette.colors.black["600"],
              borderColor: "#5a5a5a",
              "&:hover": {
                borderColor: "#3a3a3a",
                backgroundColor: theme.palette.colors.gray["800"],
              },
            }}
          >
            Close
          </Button>

          {/* When application is in IN-PROCESS state and interview feedback OR mentor feedback has been filled by at least any one interviewer
          Then the ADMIN/SUPER ADMIN can approve or reject(final rejection) the application
          Otherwise these options wouldnt be visible */}

          {isAdmin &&
            application.status == APPLICATION_STATUS.IN_PROCESS &&
            (interviewFeedbackProvided() || mentorFeedbackProvided()) && (
              <>
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => handleReject(false)}
                  startIcon={<FaTimesCircle />}
                  sx={{
                    mr: 2,
                    bgcolor: "#d32f2f",
                    "&:hover": {
                      bgcolor: "#b71c1c",
                    },
                  }}
                >
                  Final Rejection
                </Button>
                <Button
                  variant="contained"
                  onClick={handleApprove}
                  disabled={loading}
                  startIcon={!loading && <FaCheckCircle />}
                  sx={{
                    bgcolor: theme.palette.colors.green["800"],
                    "&:hover": {
                      bgcolor: theme.palette.colors.green["900"],
                    },
                    color: theme.palette.colors.white["500"],
                  }}
                >
                  {loading ? (
                    <>
                      <CircularProgress
                        size={16}
                        style={{
                          marginRight: 8,
                          color: theme.palette.colors.white[400] as string,
                        }}
                      />
                      Approving...
                    </>
                  ) : (
                    "Final Approval"
                  )}
                </Button>{" "}
              </>
            )}

          {/* When the application is just submitted and is in the state of SUBMITTED. 
          The ADMIN/SUPER ADMIN can initially accept or reject(initial rejection without interviews) the application */}

          {isAdmin && application.status == APPLICATION_STATUS.SUBMITTED && (
            <>
              <Button
                variant="contained"
                color="error"
                onClick={() => handleReject(true)}
                startIcon={<FaTimesCircle />}
                sx={{
                  mr: 2,
                  bgcolor: "#d32f2f",
                  "&:hover": {
                    bgcolor: "#b71c1c",
                  },
                }}
              >
                Initial Rejection
              </Button>
              <Button
                variant="contained"
                onClick={() => handleAccept(application.id)}
                disabled={loading}
                startIcon={!loading && <FaCheckCircle />}
                sx={{
                  bgcolor: theme.palette.colors.green["800"],
                  "&:hover": {
                    bgcolor: theme.palette.colors.green["900"],
                  },
                  color: theme.palette.colors.white["500"],
                }}
              >
                {loading ? (
                  <>
                    <CircularProgress
                      size={16}
                      style={{
                        marginRight: 8,
                        color: theme.palette.colors.white[400] as string,
                      }}
                    />
                    Accepting...
                  </>
                ) : (
                  "Initial Acceptance"
                )}
              </Button>{" "}
            </>
          )}
        </Box>
      </Dialog>

      <RejectionFeedbackModal
        open={rejectionModalOpen}
        onClose={() => setRejectionModalOpen(false)}
        initialRejection={initialRejection}
        onSubmit={handleRejectionSubmit}
        applicationId={application.id}
      />
      <ApprovalFeedbackModal
        open={approvalModalOpen}
        onClose={() => setApprovalModalOpen(false)}
        onSubmit={handleApprovalSubmit}
        applicationId={application.id}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* @ts-ignore */}
      <style jsx global>{`
        .swal-overlay-container {
          z-index: 1400 !important; /* Higher than MUI Dialog */
        }
        .swal-overlay-popup {
          z-index: 1500 !important;
        }
      `}</style>
    </>
  );
}
